import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi, clearAuthTokens, getAuthToken, setPersistentAuthTokens } from "../services/api";
import { getStoredShoppingRegion, setStoredShoppingRegion } from "../utils/retailerLinks";

const AuthContext = createContext(null);
const THEME_KEY = "hm_theme";

function resolveTheme(pref) {
  if (pref === "dark" || pref === "light") return pref;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [themePreference, setThemePreferenceState] = useState(() => localStorage.getItem(THEME_KEY) || "system");
  const [shoppingRegion, setShoppingRegionState] = useState(() => getStoredShoppingRegion());
  const [accountAvatar, setAccountAvatar] = useState(() => localStorage.getItem("hm_account_avatar") || "");

  useEffect(() => {
    const apply = () => {
      document.documentElement.dataset.theme = resolveTheme(themePreference);
    };
    apply();
    if (themePreference !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [themePreference]);

  const setThemePreference = useCallback((next) => {
    localStorage.setItem(THEME_KEY, next);
    setThemePreferenceState(next);
    document.documentElement.dataset.theme = resolveTheme(next);
  }, []);

  const setShoppingRegion = useCallback((next) => {
    setStoredShoppingRegion(next || "");
    setShoppingRegionState(getStoredShoppingRegion());
  }, []);

  const refreshUser = useCallback(async () => {
    const data = await authApi.me();
    setUser(data);
    setAccountAvatar(data?.avatar_url || "");
    localStorage.setItem("hm_account_avatar", data?.avatar_url || "");
    return data;
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      authApi.me()
        .then(data => {
          setUser(data);
          setAccountAvatar(data?.avatar_url || "");
          localStorage.setItem("hm_account_avatar", data?.avatar_url || "");
        })
        .catch(() => {
          clearAuthTokens();
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    const onSignout = () => setUser(null);
    window.addEventListener("hm_signout", onSignout);
    return () => window.removeEventListener("hm_signout", onSignout);
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    setPersistentAuthTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setAccountAvatar(data.user?.avatar_url || "");
    localStorage.setItem("hm_account_avatar", data.user?.avatar_url || "");
    return data.user;
  }, []);

  const register = useCallback(async (formData) => {
    const data = await authApi.register(formData);
    setPersistentAuthTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setAccountAvatar(data.user?.avatar_url || "");
    localStorage.setItem("hm_account_avatar", data.user?.avatar_url || "");
    return data.user;
  }, []);

  const loginWithGoogle = useCallback(async (credential, options = {}) => {
    const data = await authApi.googleAuth(credential, options);
    setPersistentAuthTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setAccountAvatar(data.user?.avatar_url || "");
    localStorage.setItem("hm_account_avatar", data.user?.avatar_url || "");
    return data;
  }, []);

  const logout = useCallback(() => {
    clearAuthTokens();
    setUser(null);
  }, []);

  const updateUser = useCallback(async (updates) => {
    const updated = await authApi.updateMe(updates);
    setUser(updated);
    if (Object.prototype.hasOwnProperty.call(updates, "avatar_url")) {
      const nextAvatar = updated?.avatar_url || updates.avatar_url || "";
      setAccountAvatar(nextAvatar);
      localStorage.setItem("hm_account_avatar", nextAvatar);
    }
    return updated;
  }, []);

  const applyAiUsage = useCallback((usage) => {
    if (!usage) return;
    setUser((current) => current ? {
      ...current,
      ai_requests_used: usage.used ?? current.ai_requests_used,
      ai_requests_limit: usage.limit ?? current.ai_requests_limit,
      reset_date: usage.reset_date ?? current.reset_date,
      plan: usage.plan ?? current.plan,
    } : current);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      loginWithGoogle,
      register,
      logout,
      updateUser,
      refreshUser,
      applyAiUsage,
      themePreference,
      setThemePreference,
      shoppingRegion,
      setShoppingRegion,
      accountAvatar,
      setAccountAvatar: (next) => {
        setAccountAvatar(next || "");
        localStorage.setItem("hm_account_avatar", next || "");
      },
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
